import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';

import { ARENA_INQUIRY_API_URL } from '@/constants/apiUrls';
import { LOCAL_LANGUAGE } from '@/constants/storageKey';
import {
  useArenaFileCategory,
  useGetArenaEventCategory,
  useGetArenaSegmentCategory,
  useGetAudienceType,
  useGetStageType,
  usePostEmailForm,
} from '@/hooks/CommonRequests';
import { InputForm } from '@/pages/inquiry/inquryStyles';
import { ArenaRequestDetails } from '@/pages/arena/index';
import ArenaClientInformation from '@/pages/arena/ArenaClientInformation';
import {
  changeDateFormat,
  arenaEmailIdPerLanguage,
  removeCommaFromNumber,
  getCodeName,
} from '@/utils/functions';
import { zodArena } from '@/utils/zodValidation';

const ArenaForm = ({ step, setStep, eventType }) => {
  const intl = useIntl();

  const navigate = useNavigate();
  const lang = localStorage.getItem(LOCAL_LANGUAGE);

  const { data: arenaEventCategory } = useGetArenaEventCategory();
  const { data: audienceType } = useGetAudienceType();

  const { data: arenaSegmentCategory } = useGetArenaSegmentCategory();
  const { data: stageType } = useGetStageType();
  const { data: arenaFile } = useArenaFileCategory();

  const [submittedData, setSubmittedData] = useState({});

  const [fileList, setFileList] = useState({
    business: [],
    other: [],
  });

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(zodArena),
    mode: 'all',
    defaultValues: {
      event_type: eventType,
      first_name: '',
      last_name: '',
      team: '',
      position: '',
      email: '',
      phone: '',
      company_name: '',
      segment: '',
      event_name: '',
      attendees: '',
      rooms: '',
      preparation_rental_start_date: '',
      preparation_rental_end_date: '',
      event_rental_start_date: '',
      event_rental_end_date: '',
      withdrawal_rental_start_date: '',
      withdrawal_rental_end_date: '',
      schedule: '',
      stage_type: '1',
      audience_type: '1',
      comments: '',
      fileList_business: [],
      fileList_other: [],
      captcha: false,
    },
  });

  const {
    mutate: postEmailForm,
    isSuccess: sendEmailSuccess,
    isPending: isEmailPending,
  } = usePostEmailForm();

  const {
    mutate: arenaMutate,
    isSuccess: sendFormSuccess,
    isPending,
  } = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(ARENA_INQUIRY_API_URL, data);
      return response.data;
    },

    onSuccess: (data) => {
      setSubmittedData((prev) => ({ ...prev, ins_date: data.data.ins_date }));
    },
  });

  const isLoading = isPending || isEmailPending;

  const watchData = watch();

  useEffect(() => {
    if (sendFormSuccess && !sendEmailSuccess) {
      const customerEmailData = {
        template_id: arenaEmailIdPerLanguage(),
        target: [{ email: submittedData?.email }],
        content: {
          first_name: submittedData?.first_name,
          last_name: submittedData?.last_name,
          phone: submittedData?.phone,
          company_name: submittedData?.company_name,
          segment: getCodeName(arenaSegmentCategory, submittedData?.segment),
          team: submittedData?.team,
          position: submittedData?.position,
          business: intl.formatMessage({ id: 'lang-inspire-arena' }),
          event_type: getCodeName(arenaEventCategory, submittedData?.event_type),
          event_name: submittedData?.event_name,
          attendees: submittedData?.attendees,
          event_rental_start_date: submittedData?.event_rental_start_date,
          event_rental_end_date: submittedData?.event_rental_end_date,
          preparation_rental_start_date: submittedData?.preparation_rental_start_date,
          preparation_rental_end_date: submittedData?.preparation_rental_end_date,
          withdrawal_rental_start_date: submittedData?.withdrawal_rental_start_date,
          withdrawal_rental_end_date: submittedData?.withdrawal_rental_end_date,
          schedule: submittedData?.schedule,
          stage_type: getCodeName(stageType, submittedData?.stage_type),
          audience_type: getCodeName(audienceType, submittedData?.audience_type),
          email: submittedData?.email,
          comments: submittedData?.comments,
          file_url1: submittedData.file_list?.[0]?.file_url,
          file_name1: submittedData.file_list?.[0]?.origin_file_name,
          file_url2: submittedData.file_list?.[1]?.file_url,
          file_name2: submittedData.file_list?.[1]?.origin_file_name,
        },
      };

      // BEFORE-BUILD: 주석 지우기
      const managerEmailData = {
        template_id: process.env.REACT_APP_ARENA_EMAIL_INSPIRE_ID,
        target: [
          { email: process.env.REACT_APP_ARENA_EMAIL_MANAGER_1 },
          { email: process.env.REACT_APP_ARENA_EMAIL_MANAGER_2 },
          { email: process.env.REACT_APP_ARENA_EMAIL_MANAGER_3 },
          { email: process.env.REACT_APP_ARENA_EMAIL_MANAGER_4 },
          { email: process.env.REACT_APP_ARENA_EMAIL_MANAGER_5 },
          { email: process.env.REACT_APP_ARENA_EMAIL_MANAGER_6 },
          { email: process.env.REACT_APP_ARENA_EMAIL_MANAGER_7 },
          // { email: process.env.REACT_APP_ARENA_EMAIL_MANAGER_10 },
        ],
        content: {
          ins_date: submittedData?.ins_date,
          first_name: submittedData?.first_name,
          last_name: submittedData?.last_name,
          phone: submittedData?.phone,
          company_name: submittedData?.company_name,
          segment: getCodeName(arenaSegmentCategory, submittedData?.segment),
          team: submittedData?.team,
          position: submittedData?.position,
          business: 'Arena',
          event_type: getCodeName(arenaEventCategory, submittedData?.event_type),
          event_name: submittedData?.event_name,
          attendees: submittedData?.attendees,
          event_rental_start_date: submittedData?.event_rental_start_date,
          event_rental_end_date: submittedData?.event_rental_end_date,
          preparation_rental_start_date: submittedData?.preparation_rental_start_date,
          preparation_rental_end_date: submittedData?.preparation_rental_end_date,
          withdrawal_rental_start_date: submittedData?.withdrawal_rental_start_date,
          withdrawal_rental_end_date: submittedData?.withdrawal_rental_end_date,
          schedule: submittedData?.schedule,
          stage_type: getCodeName(stageType, submittedData?.stage_type),
          audience_type: getCodeName(audienceType, submittedData?.audience_type),
          email: submittedData?.email,
          comments: submittedData?.comments,
          file_url1: submittedData.file_list?.[0]?.file_url,
          file_name1: submittedData.file_list?.[0]?.origin_file_name,
          file_url2: submittedData.file_list?.[1]?.file_url,
          file_name2: submittedData.file_list?.[1]?.origin_file_name,
        },
      };

      postEmailForm(customerEmailData);
      postEmailForm(managerEmailData);
    }

    if (sendEmailSuccess) {
      navigate(`/${lang}/complete`, { state: { complete: true, type: 'arena' } });
    }
  }, [
    arenaEventCategory,
    fileList,
    sendEmailSuccess,
    lang,
    navigate,
    postEmailForm,
    audienceType,
    sendFormSuccess,
    stageType,
    submittedData,
    intl,
    arenaSegmentCategory,
  ]);

  useEffect(() => {
    // NOTE: phone 쓰고 다시 지웠을 때 undefined로 떠서 validation 문구 required로 뜨는 문제
    if (watchData.phone === undefined) {
      setValue('phone', '');
    }
  }, [watchData.phone, setValue]);

  const submitHandler = useCallback(
    async (data) => {
      const { fileList_business, fileList_other, ...restData } = data;

      const businessTypeCode = arenaFile?.find(
        (el) => el.code_id === 'business_certificate'
      )?.code_value;
      const otherTypeCode = arenaFile?.find((el) => el.code_id === 'company_profile')?.code_value;

      const mappedBusinessFiles = fileList.business.map((file) => ({
        ...file,
        file_type: businessTypeCode,
      }));

      const mappedOtherFiles = fileList.other.map((file) => ({
        ...file,
        file_type: otherTypeCode,
      }));

      const changeToNumber = {
        ...restData,
        event_type: eventType,
        attendees: removeCommaFromNumber(restData.attendees),
        // file_list: fileList.business.concat(fileList.other),
        file_list: mappedBusinessFiles.concat(mappedOtherFiles),
        audience_type: restData.audience_type,
        preparation_rental_start_date: changeDateFormat(restData.preparation_rental_start_date),
        preparation_rental_end_date: changeDateFormat(restData.preparation_rental_end_date),
        event_rental_start_date: changeDateFormat(restData.event_rental_start_date),
        event_rental_end_date: changeDateFormat(restData.event_rental_end_date),
        withdrawal_rental_start_date: changeDateFormat(restData.withdrawal_rental_start_date),
        withdrawal_rental_end_date: changeDateFormat(restData.withdrawal_rental_end_date),
        language: localStorage.getItem(LOCAL_LANGUAGE),
      };

      setSubmittedData(changeToNumber);
      arenaMutate(changeToNumber);
      // console.log('--- Arena API 전송 예정 데이터 ---', changeToNumber);
    },
    [eventType, arenaMutate, fileList.business, fileList.other]
  );

  return (
    <InputForm onSubmit={handleSubmit(submitHandler)}>
      {step === 1 && (
        <ArenaClientInformation
          trigger={trigger}
          control={control}
          setStep={setStep}
          watch={watch}
          errors={errors}
          setValue={setValue}
        />
      )}

      {step === 2 && (
        <ArenaRequestDetails
          control={control}
          setStep={setStep}
          setValue={setValue}
          errors={errors}
          fileList={fileList}
          setFileList={setFileList}
          stageType={stageType}
          audienceType={audienceType}
          isPending={isLoading}
        />
      )}
    </InputForm>
  );
};

export default ArenaForm;
